import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSubscriptionPlans from "@/hooks/useSubscriptionPlans";
import {
  Plus, Search, Building2, Users as UsersIcon, MapPin, Calendar,
  CheckCircle2, AlertTriangle, PauseCircle, Archive, Clock, X, Copy, Crown, Loader2,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Normalise FastAPI / axios error payloads. Pydantic 422 responses ship
// `detail` as an **array** of `{type, loc, msg, ...}` objects, which crashes
// React the moment we hand it to <Toaster> directly. This helper coerces any
// of those shapes into a single human-readable string.
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((it) => {
        if (typeof it === "string") return it;
        const where = Array.isArray(it?.loc) ? it.loc.slice(-1)[0] : null;
        return where ? `${where}: ${it.msg}` : it?.msg || JSON.stringify(it);
      })
      .join(" · ");
  }
  return typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d);
};

// Operator-facing pill colour palette per effective status.
const STATUS_STYLES = {
  trial:     { bg: "bg-amber-100",  text: "text-amber-700",  label: { ar: "تجريبي",   en: "Trial" }, Icon: Clock },
  active:    { bg: "bg-emerald-100", text: "text-emerald-700", label: { ar: "نشط",      en: "Active" }, Icon: CheckCircle2 },
  suspended: { bg: "bg-rose-100",    text: "text-rose-700",    label: { ar: "موقوف",    en: "Suspended" }, Icon: PauseCircle },
  expired:   { bg: "bg-orange-100",  text: "text-orange-700",  label: { ar: "منتهي",    en: "Expired" }, Icon: AlertTriangle },
  archived:  { bg: "bg-stone-200",   text: "text-stone-600",   label: { ar: "مؤرشف",    en: "Archived" }, Icon: Archive },
};

const StatusBadge = ({ status, lang }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  const Ico = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <Ico size={11} />{s.label[lang === "ar" ? "ar" : "en"]}
    </span>
  );
};

const PlanBadge = ({ plan, lang }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[var(--gold-tint)] text-[var(--gold)]">
    <Crown size={10}/>{plan}
  </span>
);

// Lean modal — six required fields only, plus a couple of defaulted optional knobs.
const CreateTenantModal = ({ open, onClose, onCreated, lang, basePath }) => {
  const { plans, loading: plansLoading } = useSubscriptionPlans({ activeOnly: true });
  const [form, setForm] = useState({
    name: "", owner_name: "", owner_email: "", owner_phone: "",
    plan: "trial", country: "QA", city: "", default_branch_name: "",
    trial_days: 14,
  });
  // Default to the first plan flagged "trial-ish" once the catalog loads,
  // so the operator's first click works even after they renamed/deleted
  // the legacy `trial` code.
  useEffect(() => {
    if (!plansLoading && plans.length > 0 && !plans.find(p => p.code === form.plan)) {
      const trialish = plans.find(p => p.code === "trial") || plans[0];
      setForm(f => ({ ...f, plan: trialish.code }));
    }
  }, [plans, plansLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;
  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.owner_name || !form.owner_email) {
      toast.error(lang === "ar" ? "املأ الحقول المطلوبة" : "Fill required fields");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/tenants", form);
      toast.success(lang === "ar" ? "تم إنشاء Tenant" : "Tenant created");
      setResult(data);
      onCreated?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl" onMouseDown={onModalPanelMouseDown} data-testid="create-tenant-modal">
        {!result ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl gold-text">{lang === "ar" ? "إضافة Tenant" : "Add Tenant"}</h2>
              <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-stone-500">{lang === "ar" ? "اسم النشاط *" : "Business name *"}</label>
                <input className="input-dark mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="ct-name"/>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "اسم المالك *" : "Owner name *"}</label>
                <input className="input-dark mt-1" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} required data-testid="ct-owner"/>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "بريد المالك *" : "Owner email *"}</label>
                <input type="email" className="input-dark mt-1" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})} required data-testid="ct-email"/>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "هاتف المالك" : "Owner phone"}</label>
                <input className="input-dark mt-1" value={form.owner_phone} onChange={e => setForm({...form, owner_phone: e.target.value})} data-testid="ct-phone"/>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "الخطة *" : "Plan *"}</label>
                <select className="input-dark mt-1" value={form.plan} onChange={e => setForm({...form, plan: e.target.value})} data-testid="ct-plan">
                  {plansLoading && <option value="">…</option>}
                  {plans.map(p => (
                    <option key={p.code} value={p.code}>
                      {p.name_en} — {p.monthly_price} {p.currency || "QAR"}/mo
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "الدولة" : "Country"}</label>
                <input className="input-dark mt-1" value={form.country} onChange={e => setForm({...form, country: e.target.value})}/>
              </div>
              <div>
                <label className="text-xs text-stone-500">{lang === "ar" ? "المدينة" : "City"}</label>
                <input className="input-dark mt-1" value={form.city} onChange={e => setForm({...form, city: e.target.value})}/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-stone-500">{lang === "ar" ? "اسم الفرع الافتراضي" : "Default branch name"}</label>
                <input className="input-dark mt-1" placeholder={lang === "ar" ? "الفرع الرئيسي" : "Main Branch"} value={form.default_branch_name} onChange={e => setForm({...form, default_branch_name: e.target.value})}/>
              </div>
              {form.plan === "trial" && (
                <div className="col-span-2">
                  <label className="text-xs text-stone-500">{lang === "ar" ? "أيام التجربة" : "Trial days"}</label>
                  <input type="number" className="input-dark mt-1" value={form.trial_days} onChange={e => setForm({...form, trial_days: parseInt(e.target.value) || 14})}/>
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 mt-3">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                <button type="submit" disabled={saving} className="btn-gold px-5 py-2 rounded-lg flex items-center gap-2" data-testid="ct-submit">
                  {saving ? <Loader2 className="animate-spin" size={14}/> : <Plus size={14}/>}
                  {lang === "ar" ? "إنشاء" : "Create"}
                </button>
              </div>
            </form>
          </>
        ) : (
          <CreateResult result={result} lang={lang} onClose={onClose} basePath={basePath}/>
        )}
      </div>
    </div>
  );
};

const CreateResult = ({ result, lang, onClose, basePath }) => {
  const navigate = useNavigate();
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success(lang === "ar" ? "تم النسخ" : "Copied"); };
  // Use the basePath the caller is currently in (platform-admin vs legacy).
  const openPath = `${basePath}/${result.tenant_id}`;
  return (
    <div data-testid="create-tenant-success">
      <div className="text-center mb-4">
        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3"><CheckCircle2 size={28}/></div>
        <h3 className="font-heading text-xl gold-text">{lang === "ar" ? "تم إنشاء Tenant بنجاح" : "Tenant created successfully"}</h3>
        <p className="text-xs text-stone-500 mt-1">{lang === "ar" ? "احفظ بيانات المالك المؤقتة قبل الإغلاق" : "Save the owner credentials before closing"}</p>
      </div>
      <div className="space-y-2 text-sm">
        {[
          { label: "Tenant ID", v: result.tenant_id },
          { label: "Slug", v: result.slug },
          { label: lang === "ar" ? "اسم المستخدم" : "Username", v: result.owner_username },
          { label: lang === "ar" ? "كلمة المرور المؤقتة" : "Temp password", v: result.owner_temp_password, highlight: true },
        ].map(({ label, v, highlight }) => (
          <div key={label} className={`flex items-center justify-between rounded-lg p-2.5 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-100"}`}>
            <span className="text-xs text-stone-500">{label}</span>
            <div className="flex items-center gap-2">
              <code className="font-mono text-xs">{v}</code>
              <button onClick={() => copy(v)} className="text-stone-400 hover:text-stone-700"><Copy size={13}/></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-100">{lang === "ar" ? "إغلاق" : "Close"}</button>
        <button onClick={() => navigate(openPath)} className="btn-gold px-5 py-2 rounded-lg">{lang === "ar" ? "فتح Tenant" : "Open Tenant"}</button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, Icon, color = "bg-stone-100 text-stone-700" }) => (
  <div className="bg-white rounded-2xl border border-stone-100 p-4 shadow-sm">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${color}`}><Icon size={18}/></div>
    <div className="text-xs text-stone-500">{label}</div>
    <div className="text-2xl font-bold text-stone-800 num-display">{value}</div>
  </div>
);

export default function TenantsAdmin() {
  // i18n via the global LanguageContext (auto-reactive across pages).
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  // The component is mounted both under `/admin/tenants` (legacy bookmark)
  // and under `/platform-admin/tenants` (new layout). Detail links must
  // honour whichever base the operator is currently in so the platform-admin
  // sidebar stays visible.
  const loc = useLocation();
  const baseDetailPath = loc.pathname.startsWith("/platform-admin")
    ? "/platform-admin/tenants"
    : "/admin/tenants";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Initial filter values come from the URL (?plan=basic&status=trial&expiry=7d)
  // so the Dashboard cards can deep-link straight into a pre-filtered list.
  const initial = useMemo(() => new URLSearchParams(loc.search), []); // eslint-disable-line
  const [statusFilter, setStatusFilter] = useState(initial.get("status") || "");
  const [planFilter, setPlanFilter] = useState(initial.get("plan") || "");
  const [expiryFilter, setExpiryFilter] = useState(initial.get("expiry") || "");
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (planFilter) params.plan = planFilter;
      if (search) params.search = search;
      const { data } = await api.get("/admin/tenants", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Re-fetch on any filter change (debounced for the search box).
  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, planFilter, search]);

  // Apply the client-side expiry filter. The backend doesn't have a
  // date-range param yet — for a platform with ~hundreds of tenants the
  // client-side filter is fast enough and avoids backend churn. If the
  // catalog grows past ~5k tenants, push this to the API.
  const visibleItems = useMemo(() => {
    if (!expiryFilter) return items;
    const now = Date.now();
    const day = 86400000;
    return items.filter(t => {
      const iso = t.subscription_ends_at || t.trial_ends_at;
      if (!iso) return expiryFilter === "none";
      const end = new Date(iso).getTime();
      const diff = (end - now) / day;
      switch (expiryFilter) {
        case "expired":   return diff < 0;
        case "7d":        return diff >= 0 && diff <= 7;
        case "14d":       return diff > 7 && diff <= 14;
        case "30d":       return diff > 14 && diff <= 30;
        case "60d":       return diff > 30 && diff <= 60;
        case "90plus":    return diff > 90;
        case "none":      return false;  // matched above when iso=null
        default:          return true;
      }
    });
  }, [items, expiryFilter]);

  const stats = useMemo(() => {
    const total = visibleItems.length;
    const active = visibleItems.filter(t => t.effective_status === "active").length;
    const trial = visibleItems.filter(t => t.effective_status === "trial").length;
    const suspended = visibleItems.filter(t => ["suspended", "expired"].includes(t.effective_status)).length;
    return { total, active, trial, suspended };
  }, [visibleItems]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5" data-testid="tenants-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl gold-text">{isAr ? "الـ Tenants والاشتراكات" : "Tenants & Subscriptions"}</h1>
          <p className="text-xs text-stone-500 mt-0.5">{isAr ? "إدارة الصالونات المُسجَّلة على المنصة" : "Manage salons registered on the platform"}</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn-gold px-5 py-2.5 rounded-xl flex items-center gap-2 shadow-md" data-testid="open-create-tenant">
          <Plus size={16}/>{isAr ? "إضافة Tenant" : "Add Tenant"}
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={isAr ? "إجمالي" : "Total"} value={stats.total} Icon={Building2} color="bg-indigo-100 text-indigo-700"/>
        <Stat label={isAr ? "نشط" : "Active"} value={stats.active} Icon={CheckCircle2} color="bg-emerald-100 text-emerald-700"/>
        <Stat label={isAr ? "تجريبي" : "Trial"} value={stats.trial} Icon={Clock} color="bg-amber-100 text-amber-700"/>
        <Stat label={isAr ? "موقوف/منتهي" : "Susp./Expired"} value={stats.suspended} Icon={AlertTriangle} color="bg-rose-100 text-rose-700"/>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3 sm:p-4 shadow-sm flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-stone-400 pointer-events-none"/>
          <input
            className="input-dark ps-9 text-sm"
            placeholder={isAr ? "بحث بالاسم أو الإيميل أو الهاتف..." : "Search by name, email, phone..."}
            value={search} onChange={e => setSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>
        <select className="input-dark text-sm w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="status-filter">
          <option value="">{isAr ? "كل الحالات" : "All statuses"}</option>
          <option value="trial">{isAr ? "تجريبي" : "Trial"}</option>
          <option value="active">{isAr ? "نشط" : "Active"}</option>
          <option value="suspended">{isAr ? "موقوف" : "Suspended"}</option>
          <option value="expired">{isAr ? "منتهي" : "Expired"}</option>
          <option value="archived">{isAr ? "مؤرشف" : "Archived"}</option>
        </select>
        <select className="input-dark text-sm w-auto" value={planFilter} onChange={e => setPlanFilter(e.target.value)} data-testid="plan-filter">
          <option value="">{isAr ? "كل الخطط" : "All plans"}</option>
          <option value="trial">{isAr ? "تجريبي" : "Trial"}</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
          <option value="legacy">Legacy</option>
          <option value="free">Free</option>
        </select>
        <select
          className="input-dark text-sm w-auto"
          value={expiryFilter}
          onChange={e => setExpiryFilter(e.target.value)}
          data-testid="expiry-filter"
          title={isAr ? "تصفية حسب تاريخ الانتهاء" : "Filter by expiry"}
        >
          <option value="">{isAr ? "كل تواريخ الانتهاء" : "All expiry dates"}</option>
          <option value="expired">{isAr ? "منتهي" : "Expired"}</option>
          <option value="7d">{isAr ? "ينتهي خلال 7 أيام" : "Expires within 7 days"}</option>
          <option value="14d">{isAr ? "خلال 8-14 يوم" : "8-14 days"}</option>
          <option value="30d">{isAr ? "خلال 15-30 يوم" : "15-30 days"}</option>
          <option value="60d">{isAr ? "خلال 31-60 يوم" : "31-60 days"}</option>
          <option value="90plus">{isAr ? "أكثر من 90 يوم" : "90+ days"}</option>
          <option value="none">{isAr ? "بدون تاريخ" : "No date set"}</option>
        </select>
        {(statusFilter || planFilter || expiryFilter || search) && (
          <button
            onClick={() => { setStatusFilter(""); setPlanFilter(""); setExpiryFilter(""); setSearch(""); }}
            className="text-xs text-stone-500 hover:text-stone-700 px-2"
            data-testid="clear-filters"
          >
            {isAr ? "مسح ✕" : "Clear ✕"}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-stone-400 text-sm flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={14}/>{isAr ? "جارٍ التحميل..." : "Loading..."}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">
            {items.length === 0
              ? (isAr ? "لا توجد بيانات" : "No data")
              : (isAr ? "لا توجد نتائج مطابقة للفلتر" : "No tenants match the filter")}
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start p-3">{isAr ? "النشاط" : "Tenant"}</th>
                  <th className="text-start p-3">{isAr ? "المالك" : "Owner"}</th>
                  <th className="p-3"><MapPin size={11} className="inline"/></th>
                  <th className="p-3"><UsersIcon size={11} className="inline"/></th>
                  <th className="p-3">{isAr ? "الحالة" : "Status"}</th>
                  <th className="p-3">{isAr ? "الخطة" : "Plan"}</th>
                  <th className="p-3">{isAr ? "تجربة/تجديد" : "Trial/Renew"}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map(t => (
                  <tr key={t.tenant_id} className="border-t border-stone-100 hover:bg-stone-50/60 transition" data-testid={`tenant-row-${t.tenant_id}`}>
                    <td className="p-3">
                      <div className="font-semibold text-stone-800">{(t.name || {}).en || t.slug}</div>
                      <div className="text-[10px] text-stone-400 font-mono">{t.slug}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-stone-700">{(t.contact || {}).owner_name || "—"}</div>
                      <div className="text-[10px] text-stone-400">{(t.contact || {}).owner_email}</div>
                    </td>
                    <td className="p-3 text-center num-display font-semibold">{t.branches_count}</td>
                    <td className="p-3 text-center num-display font-semibold">{t.users_count}</td>
                    <td className="p-3 text-center"><StatusBadge status={t.effective_status} lang={lang}/></td>
                    <td className="p-3 text-center"><PlanBadge plan={t.plan} lang={lang}/></td>
                    <td className="p-3 text-center text-xs">
                      {(() => {
                        // Source-of-truth column: prefer the active
                        // subscription window for paid tenants; fall
                        // back to the trial end for trial tenants. Add
                        // a small label so the operator can tell at a
                        // glance whether the row is a trial or a paid
                        // subscription, with countdown coloring once it
                        // gets close to expiring.
                        const subEnd = t.subscription_ends_at;
                        const trialEnd = t.trial_ends_at;
                        const isPaid = !!subEnd;
                        const iso = subEnd || trialEnd;
                        if (!iso) return <span className="text-stone-400">—</span>;
                        const end = new Date(iso);
                        const daysLeft = Math.ceil((end - new Date()) / 86400000);
                        const tone =
                          daysLeft < 0 ? "text-rose-600 font-semibold"
                          : daysLeft <= 7 ? "text-amber-600 font-semibold"
                          : "text-stone-600";
                        return (
                          <div className="flex flex-col items-center gap-0.5 leading-tight">
                            <span className={tone}>{end.toLocaleDateString(isAr ? "ar-EG" : "en-US")}</span>
                            <span className="text-[9px] uppercase tracking-wider text-stone-400">
                              {isPaid ? (isAr ? "تجديد" : "renew") : (isAr ? "تجربة" : "trial")}
                              {" · "}
                              <span className={tone}>
                                {daysLeft < 0
                                  ? (isAr ? `منتهٍ ${-daysLeft}ي` : `${-daysLeft}d ago`)
                                  : (isAr ? `${daysLeft} يوم` : `${daysLeft}d`)}
                              </span>
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3 text-end">
                      <Link
                        to={`${baseDetailPath}/${t.tenant_id}`}
                        className="text-xs px-3 py-1.5 rounded-lg bg-[var(--gold-tint)] text-[var(--gold)] hover:bg-[var(--gold-tint-strong)] transition font-medium"
                        data-testid={`open-tenant-${t.tenant_id}`}
                      >
                        {isAr ? "فتح" : "Open"}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateTenantModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={() => load()} lang={lang} basePath={baseDetailPath}/>
    </div>
  );
}
